import * as RadPopover from '@radix-ui/react-popover'
import { useAtom, useAction } from '@reatom/npm-react'
import React, { useCallback, useMemo, useState } from 'react'
import styled, { keyframes } from 'styled-components'

import { useHapticFeedback } from '@blade/ui-kit'
import {
  setTrendingStrategyAction,
  trendingStrategiesStateAtom,
  trendingStrategyAtom,
} from '@root/features/trending-pairs'
import { useTranslation } from '@root/shared/localization'
import { ArrowDown2Icon, ArrowUp2Icon } from '@root/shared/ui-kit'

import {
  exploreColumnsAtom,
  setLayoutColumnAction,
} from '../../atoms/reatom-explore-layout'
import {
  EXPLORE_SCREEN_TABS,
  ExploreFeedType as FeedType,
  ExploreTypeMap,
} from '../../model/explore-feed-type'

import type { ExploreLayout } from '../../model'
import type { ExploreFeedType } from '../../model/explore-feed-type'

type ExploreTypeSelectProps = {
  column: ExploreLayout.Column
}

export const ExploreTypeSelect = ({ column }: ExploreTypeSelectProps) => {
  const { t } = useTranslation()
  const [columns] = useAtom(exploreColumnsAtom)
  const setLayoutColumn = useAction(setLayoutColumnAction)
  const [strategiesState] = useAtom(trendingStrategiesStateAtom)
  const [strategy] = useAtom(trendingStrategyAtom)
  const setStrategy = useAction(setTrendingStrategyAction)
  const selectedFeed = columns[column]!
  const [open, setOpen] = useState(false)
  const haptics = useHapticFeedback()

  const menuItems = useMemo(() => {
    const active = columns.map((feed) => ({
      feed,
      variant: 'active' as const,
    }))
    const inactive = EXPLORE_SCREEN_TABS.filter(
      (feed) => !columns.includes(feed),
    ).map((feed) => ({ feed, variant: 'inactive' as const }))
    return [...active, ...inactive]
  }, [columns])

  const handleSelect = useCallback(
    (item: ExploreFeedType) => {
      haptics.impactOccurred('light')
      setLayoutColumn(column, item)
      setOpen(false)
    },
    [column, haptics, setLayoutColumn],
  )

  const handleTriggerClick = useCallback(() => {
    haptics.impactOccurred('light')
    setOpen((x) => !x)
  }, [haptics])

  const value = ExploreTypeMap[selectedFeed]

  // Only the trending column has more than one way to be ordered, and a list
  // of one is not a choice.
  const strategies =
    selectedFeed === FeedType.Trending ? (strategiesState.value ?? []) : []
  const ranking = strategies.find((item) => item.id === strategy)

  return (
    <Root open={open} onOpenChange={setOpen}>
      <Trigger asChild>
        <Button
          type="button"
          onClick={handleTriggerClick}
          style={{
            '--item-color': value.color,
          }}
        >
          <value.Icon size={16} />
          <Label>
            {t(value.label)}
            {strategies.length > 1 && ranking ? (
              <Ranking>{ranking.label}</Ranking>
            ) : undefined}
          </Label>
          <ArrowIcon>
            <ArrowDown2Icon size={12} />
          </ArrowIcon>
        </Button>
      </Trigger>
      <RadPopover.Portal>
        <Content side="bottom" align="start">
          <Button
            aria-expanded
            onClick={handleTriggerClick}
            type="button"
            style={{
              '--item-color': value.color,
            }}
          >
            <value.Icon size={16} />
            <Label>
              {t(value.label)}
              {strategies.length > 1 && ranking ? (
                <Ranking>{ranking.label}</Ranking>
              ) : undefined}
            </Label>
            <ArrowIcon>
              <ArrowUp2Icon size={12} />
            </ArrowIcon>
          </Button>
          {menuItems.map(({ feed, variant }) => {
            const itemData = ExploreTypeMap[feed]

            return (
              <Item
                key={feed}
                aria-active={variant === 'active' && feed === selectedFeed}
                aria-current={variant === 'active' ? true : undefined}
                aria-inactive={variant === 'inactive' ? true : undefined}
                onClick={() => handleSelect(feed)}
                style={{
                  '--item-color': itemData.color,
                }}
              >
                <itemData.Icon size={16} /> {t(itemData.label)}
              </Item>
            )
          })}

          {strategies.length > 1 ? (
            <>
              <SectionLabel>{t('explore.ranked-by')}</SectionLabel>
              {strategies.map((item) => (
                <Item
                  key={item.id}
                  aria-active={item.id === strategy ? true : undefined}
                  aria-current={item.id === strategy ? true : undefined}
                  onClick={() => {
                    haptics.impactOccurred('light')
                    setStrategy(item.id)
                    setOpen(false)
                  }}
                  style={{ '--item-color': value.color }}
                >
                  <Tick data-on={item.id === strategy ? true : undefined} />
                  {item.label}
                </Item>
              ))}
            </>
          ) : undefined}
        </Content>
      </RadPopover.Portal>
    </Root>
  )
}

const contentShow = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const Root = styled(RadPopover.Root)``
const Trigger = styled(RadPopover.Trigger)``

const Button = styled.button`
  all: unset;
  background: transparent;
  border-radius: 0;

  color: var(--text-12);
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;

  padding: 5px 7px;
  gap: 5px;

  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  cursor: pointer;
  border-radius: 8px;

  > svg {
    color: var(--item-color);
  }

  transition: all 0.15s ease-in-out;

  &:hover {
    background: var(--backgroundA-2);
  }

  &[aria-expanded='true'] {
    background: transparent;

    &:hover {
      background: transparent;
    }
  }
`

const ArrowIcon = styled.div`
  display: grid;
  place-items: center;
  margin-inline-start: 5px;
`

const Content = styled(RadPopover.Content)`
  border-radius: 8px;
  background: rgba(var(--background-1-rgb), 0.85);
  backdrop-filter: blur(40px);
  margin-top: -28px;
  margin-left: -2px;

  box-shadow: 2px 2px 9px 1px rgb(0 0 0 / 30%);
  z-index: 2;
  padding: 2px;
  gap: 2px;
  display: grid;
  grid-template-columns: 1fr;

  animation-duration: 200ms;
  animation-name: ${contentShow};
`

const Item = styled.div`
  background: var(--backgroundA-2);
  border-radius: 6px;

  color: var(--text-9);
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;

  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 5px;
  padding: 5px 7px;
  height: 30px;

  transition: all 0.15s ease-in-out;

  svg {
    transition: all 0.15s ease-in-out;
    color: var(--text-9);
  }

  &:hover {
    cursor: pointer;
    color: var(--text-12);
    background: var(--backgroundA-3);
    svg {
      color: var(--item-color);
    }
  }

  &[aria-current='true'] {
    color: var(--text-12);

    svg {
      color: var(--item-color);
    }
  }
  &[aria-active='true'] {
    background: var(--backgroundA-3);
  }
  &[aria-inactive] {
    background: transparent;
    border: 1px solid var(--backgroundA-2);
    &:hover {
      background: var(--backgroundA-3);
    }
  }
`

/**
 * The feed's name, with the ranking it is showing said quietly after it.
 *
 * One line rather than a pill beside it: a pill in this row collided with
 * the popover's own header and read as a second control, which it is not —
 * it is what the word "Trending" currently means.
 */
const Label = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  min-width: 0;
`

const Ranking = styled.span`
  color: var(--text-9);
  font-size: 11px;
  font-weight: 500;
`

/**
 * Where the menu stops listing feeds and starts listing orderings.
 *
 * Aligned with the rows rather than set in capitals over a gap: the gap was
 * the popover's own translucency, so the cards behind it showed through and
 * the words floated on the page.
 */
const SectionLabel = styled.div`
  color: var(--text-9);
  font-size: 10px;
  font-weight: 500;
  line-height: 14px;

  padding: 6px 7px 2px;
  margin-top: 2px;
  border-top: 1px solid var(--backgroundA-2);
`

/** A dot where the other rows have an icon, so the columns still line up. */
const Tick = styled.span`
  width: 16px;
  display: grid;
  place-items: center;

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--text-9);
    transition: all 0.15s ease-in-out;
  }

  &[data-on]::before {
    background: var(--item-color);
    box-shadow: 0 0 6px 0 var(--item-color);
  }
`
